import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import useShouldShowAds from '../../hooks/useShouldShowAds';
import { pickAffiliateProducts } from '../../utils/pickAffiliateProducts';

/**
 * Affiliate rail for page gutters (desktop).
 * Hidden for premium users and below `lg`.
 * `size`: "default" | "compact"
 * `layout`: "stack" | "grid" (2-column grid, e.g. 2×2 for 4 products)
 */
export default function AffiliateSidebarRail({
  placement = 'sidebar',
  productCount = 2,
  salt = 0,
  size = 'default',
  layout = 'stack',
  /** When false, parent controls visibility (e.g. absolute desktop gutter). */
  desktopOnly = true,
  className = '',
}) {
  const showAds = useShouldShowAds();
  const products = useMemo(
    () => pickAffiliateProducts(productCount, salt + placement.length),
    [placement, productCount, salt],
  );

  if (!showAds || products.length === 0) return null;

  const trackClick = (product) => {
    posthog.capture('internal_ad_click', {
      placement,
      format: layout === 'grid' ? 'sidebar_grid' : 'sidebar_rail',
      product_id: product.id,
      product_title: product.title,
    });
  };

  const compact = size === 'compact';
  const isGrid = layout === 'grid';

  let widthClass = 'w-40 shrink-0 xl:w-44';
  if (isGrid && compact) {
    widthClass = 'w-52 shrink-0';
  } else if (isGrid) {
    widthClass = 'w-56 shrink-0 xl:w-60';
  } else if (compact) {
    widthClass = 'w-28 shrink-0';
  }
  const visibilityClass = desktopOnly ? 'hidden lg:block' : '';

  return (
    <aside
      className={`${visibilityClass} ${widthClass} ${className}`}
      aria-label="Sponsored recommendations"
    >
      <div className={`${desktopOnly ? 'sticky top-24' : ''} ${compact ? 'space-y-2' : 'space-y-4'}`}>
        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
          Sponsored
        </p>
        <ul
          className={
            isGrid
              ? 'grid grid-cols-2 gap-2'
              : compact
                ? 'space-y-3'
                : 'space-y-4'
          }
        >
          {products.map((product) => (
            <li key={`${placement}-${product.id}`}>
              <a
                href={product.affiliateUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackClick(product)}
                className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-800"
              >
                <div
                  className={`flex w-full items-center justify-center overflow-hidden bg-white shadow-sm ring-1 ring-gray-100 ${
                    isGrid || compact
                      ? 'aspect-square max-h-24'
                      : 'aspect-[2/3]'
                  }`}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-contain transition-opacity group-hover:opacity-90"
                    />
                  ) : null}
                </div>
                <p
                  className={`mt-1 font-medium leading-snug text-gray-800 group-hover:text-violet-900 ${
                    isGrid || compact
                      ? 'line-clamp-2 text-[10px]'
                      : 'line-clamp-3 text-xs'
                  }`}
                >
                  {product.title}
                </p>
                <p
                  className={`mt-0.5 text-violet-900 ${
                    isGrid || compact ? 'text-[10px]' : 'text-[11px]'
                  }`}
                >
                  Amazon →
                </p>
              </a>
            </li>
          ))}
        </ul>
        <Link
          to="/shop"
          className={`block text-gray-500 hover:text-violet-900 hover:underline ${
            compact || isGrid ? 'text-[10px]' : 'text-[11px]'
          }`}
        >
          More in shop
        </Link>
      </div>
    </aside>
  );
}
